import React, { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVerticalIcon } from 'lucide-react';
interface RankingItem {
  id: string;
  text: string;
}
interface SortableItemProps {
  id: string;
  text: string;
  index: number;
}
function SortableItem({
  id,
  text,
  index
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return <div ref={setNodeRef} style={style} className={`flex items-center gap-3 p-4 bg-white border-2 border-gray-300 rounded-lg ${isDragging ? 'shadow-lg opacity-50' : 'shadow-sm'}`} data-id="element-98">
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none" aria-label="Drag handle" data-id="element-99">
        <GripVerticalIcon className="w-5 h-5 text-gray-400" data-id="element-100" />
      </div>
      <div className="flex items-center gap-3 flex-1" data-id="element-101">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-100 text-teal-700 font-bold text-sm" data-id="element-102">
          {index + 1}
        </span>
        <span className="font-medium text-gray-900" data-id="element-103">{text}</span>
      </div>
    </div>;
}
interface RankingQuestionProps {
  question: string;
  items: string[];
}
export function RankingQuestion({
  question,
  items: initialItems
}: RankingQuestionProps) {
  const [items, setItems] = useState<RankingItem[]>(initialItems.map((text, index) => ({
    id: `item-${index}`,
    text
  })));
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      setItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  return <div className="space-y-4" data-id="element-104">
      <h3 className="text-lg font-semibold text-gray-900" data-id="element-105">{question}</h3>
      <p className="text-sm text-gray-600" data-id="element-106">Drag to reorder by priority</p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} data-id="element-107">
        <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy} data-id="element-108">
          <div className="space-y-3" data-id="element-109">
            {items.map((item, index) => <SortableItem key={item.id} id={item.id} text={item.text} index={index} data-id="element-110" />)}
          </div>
        </SortableContext>
      </DndContext>
    </div>;
}